import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import api from '../lib/api';
import { usePersistentFilters } from '../lib/persistentFilters';
import { FileDown, Plus } from 'lucide-react';
import { exportDashboardToPDF } from '../lib/pdf-export';
import { usePermissions, PermissionGuard } from '../contexts/PermissionsContext';
import { toast } from 'sonner';
import OverviewDashboard from './dashboard/OverviewDashboard';
import ItemWiseDashboard from './dashboard/ItemWiseDashboard';
import { useCompany } from '../contexts/CompanyContext';

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const [activeTab, setActiveTab] = usePersistentFilters('dashboard.tab', 'overview');
  const [filterOptions, setFilterOptions] = useState({
    suppliers: [], qualities: [], items: [], subqualities: []
  });
  const [filters, setFilters] = usePersistentFilters('dashboard.filters', {
    date_from: '', date_to: '', supplier_id: '', quality_id: '',
    subquality_id: '', item_id: '', status: '', lr_status_filter: ''
  });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const res = await api.get('/filter-options/all');
        setFilterOptions({
          suppliers: res.data.suppliers || [],
          qualities: res.data.qualities || [],
          items: res.data.items || [],
          subqualities: res.data.subqualities || []
        });
      } catch {
        toast.error('Failed to fetch filter options');
      }
    };
    fetchFilterOptions();
  }, [activeCompany?.company_id]);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const endpoint = activeTab === 'overview' ? '/dashboard/overview-data' : '/dashboard/itemwise-data';
      const res = await api.get(`${endpoint}?${params.toString()}`);
      await exportDashboardToPDF(activeTab, res.data, filters, filterOptions);
      toast.success('PDF exported successfully');
    } catch (err) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="manager-dashboard" className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Dashboard
            </h1>
            <p className="text-slate-600 mt-1">Welcome back, {user?.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <PermissionGuard module="grey_orders" permission="create">
            <Button
              data-testid="new-order-btn"
              onClick={() => navigate('/grey-orders/create')}
              className="bg-slate-900 hover:bg-slate-800"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Grey Order
            </Button>
            </PermissionGuard>
            <PermissionGuard module="dashboard" permission="export_pdf">
            <Button
              data-testid="export-pdf-btn"
              variant="outline"
              onClick={handleExportPDF}
              disabled={exporting}
            >
              <FileDown className="h-4 w-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export to PDF'}
            </Button>
            </PermissionGuard>
          </div>
        </div>
      </div>

      {/* Tab Switcher */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList data-testid="dashboard-tabs" className="bg-slate-100">
          <TabsTrigger data-testid="tab-overview" value="overview" className="data-[state=active]:bg-white">
            Overview Dashboard
          </TabsTrigger>
          <TabsTrigger data-testid="tab-itemwise" value="itemwise" className="data-[state=active]:bg-white">
            Item-wise Dashboard
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewDashboard filters={filters} setFilters={setFilters} filterOptions={filterOptions} />
        </TabsContent>

        <TabsContent value="itemwise">
          <ItemWiseDashboard filters={filters} setFilters={setFilters} filterOptions={filterOptions} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ManagerDashboard;
